import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { canInstallPWA, showInstallPrompt } from "./InstallPrompt";

export default function InstallButton() {
  const { darkMode } = useTheme();
  const [showButton, setShowButton] = useState(false);
  const [deviceType, setDeviceType] = useState("desktop");
  const [browser, setBrowser] = useState("");

  useEffect(() => {
    const checkInstallability = () => {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
      const canInstall = canInstallPWA();

      setShowButton(!isStandalone && canInstall);

      // Detect device type
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      setDeviceType(isMobile ? "mobile" : "desktop");

      // Detect browser for desktop
      if (!isMobile) {
        const userAgent = navigator.userAgent;
        if (userAgent.indexOf("Chrome") > -1) setBrowser("Chrome");
        else if (userAgent.indexOf("Safari") > -1) setBrowser("Safari");
        else if (userAgent.indexOf("Firefox") > -1) setBrowser("Firefox");
        else if (userAgent.indexOf("Edg") > -1) setBrowser("Edge");
        else setBrowser("Browser");
      }
    };

    checkInstallability();

    // Listen for changes
    const listeners = ["appinstalled", "beforeinstallprompt", "load"];

    listeners.forEach((event) => {
      window.addEventListener(event, checkInstallability);
    });

    // Check for service worker messages
    const handleMessage = (event) => {
      if (event.data && event.data.type === "INSTALL_AVAILABLE") {
        checkInstallability();
      }
    };

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", handleMessage);
    }

    return () => {
      listeners.forEach((event) => {
        window.removeEventListener(event, checkInstallability);
      });

      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", handleMessage);
      }
    };
  }, []);

  const getButtonText = () => {
    if (deviceType === "mobile") {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "Add to Home Screen" : "Install App";
    } else {
      return `Install for ${browser || "Desktop"}`;
    }
  };

  if (!showButton) return null;

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={showInstallPrompt}
      className={`flex items-center px-4 py-2 rounded-full ${
        darkMode
          ? "bg-blue-600 hover:bg-blue-500 text-white"
          : "bg-blue-100 hover:bg-blue-200 text-blue-800"
      } transition-colors`}
      aria-label="Install App"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 mr-2"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 12h14M12 5l7 7-7 7"
        />
      </svg>
      {getButtonText()}
    </motion.button>
  );
}
